<template>
  <v-container fluid>
    <v-row>
      <!-- Jira Host URL -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Host URL
            <v-spacer></v-spacer>
            <v-btn
              small
              color="primary"
              @click="showJiraAccessTokenDialog = true"
              v-if="!hasAccess"
            >
              Generate Access Token
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-text-field
              v-model="jiraUrl"
              :disabled="!hasAccess"
              dense
              hint="Example: https://jira-hostname.atlassian.net"
              label="Jira URL"
            ></v-text-field>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!jiraUrl || !hasAccess"
              color="primary"
              @click="getProjects"
            >
              Get Projects
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Host URL -->
      <!-- Jira Projects Selector -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Project
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-autocomplete
              dense
              :disabled="!projects.length || !hasAccess"
              :items="projects"
              v-model="selectedProject"
              @change="getBoards"
              item-text="name"
              item-value="key"
            >
              <template v-slot:item="data">
                <v-list-item-avatar>
                  <img :src="data.item.icon">
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>
                    {{ data.item.name }}({{ data.item.key }})
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!selectedProject || !hasAccess"
              color="primary"
              @click="getBoards"
            >
              Get Boards
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Projects Selector -->
      <!-- Jira Boards Selector -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Boards
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-autocomplete
              dense
              :disabled="!boards.length || !hasAccess"
              :items="boards"
              v-model="selectedBoard"
              @change="getSprints"
              item-text="name"
              item-value="id"
            >
              <template v-slot:item="data">
                <v-list-item-content>
                  <v-list-item-title>
                    {{ data.item.name }}({{ data.item.type }})
                  </v-list-item-title>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!selectedBoard || !hasAccess"
              color="primary"
              @click="getSprints"
            >
              Get Sprints
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Boards Selector -->
      <!-- Jira Sprint Selector -->
      <v-col cols="12" md="4" lg="3">
        <v-card
          height="100%"
        >
          <v-card-title>
            Jira Sprints
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <v-autocomplete
              dense
              :disabled="!sprints.length || !hasAccess"
              :items="sprints"
              v-model="selectedSprint"
              item-text="name"
              item-value="id"
              @change="getVelocity"
            >
              <template v-slot:item="data">
                <v-list-item-content>
                    <v-list-item-title>
                      {{data.item.name}}({{ data.item | sprintState }})
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      {{ data.item.startDate | dateFormat }} - {{ data.item.endDate | dateFormat }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-divider></v-divider>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!selectedSprint || !hasAccess"
              color="primary"
              @click="getVelocity"
            >
              Get Velocity
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <!-- /Jira Sprint Selector -->
    </v-row>
    <v-row>
      <!-- Velocity -->
      <v-col cols="12" md="4" lg="3">
        <v-card>
          <v-card-title>
            Velocity
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <template v-if="velocity">
              Sum of <b>All</b> Story points: <b>{{ velocity.totalAll }}</b><br>
              Sum of <b>Done</b> Story points: <b>{{ velocity.totalDone }}</b>
            </template>
            <template v-else>
              Choose Sprint to get Velocity
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Velocity -->
      <!-- Counts -->
      <v-col cols="12" md="4" lg="3">
        <v-card>
          <v-card-title>
            Counts by issue types
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <template v-if="velocity">
              <div v-for="(count, key) in velocity.totalByTypes" :key="key">
                <b>{{ count.type }}: </b>{{ count.count }}
              </div>
            </template>
            <template v-else>
              Choose Sprint to get Velocity
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Counts -->
      <!-- Blocked/On Hold/Hold/Flagged -->
      <v-col cols="12" md="4" lg="3">
        <v-card>
          <v-card-title>
            Counts by issue status
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text>
            <template v-if="velocity">
              <v-row>
                <v-col>
                  <div>
                    <b>Blocked: </b>{{ velocity.totalByStatuses ? velocity.totalByStatuses.blocked : 0 }}
                  </div>
                  <div>
                    <b>On Hold: </b>{{ velocity.totalByStatuses ? velocity.totalByStatuses.onhold : 0 }}
                  </div>
                </v-col>
                <v-col>
                  <div>
                    <b>Flagged: </b>{{ velocity.totalByStatuses ? velocity.totalByStatuses.flagged : 0 }}
                  </div>
                </v-col>
              </v-row>
            </template>
            <template v-else>
              Choose Sprint to get Velocity
            </template>
          </v-card-text>
        </v-card>
      </v-col>
      <!-- /Blocked/On Hold/Hold/Flagged -->
    </v-row>
    <JiraApiTokenDialog
      v-model="showJiraAccessTokenDialog"
      @successSaved="onTokenSaved"
    />
  </v-container>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'

export default {
  name: 'JiraVelocity',
  components: {
    JiraApiTokenDialog: () => import('@/components/global/JiraApiTokenDialog')
  },
  data () {
    return {
      hasAccess: false,
      showJiraAccessTokenDialog: false,
      jiraUrl: 'https://devcomprojects.atlassian.net/',
      projects: [],
      boards: [],
      sprints: [],
      velocity: null,
      selectedProject: null,
      selectedBoard: null,
      selectedSprint: null,
      storyPointFieldId: null,
      countsOfTasks: null
    }
  },
  computed: {
    user () {
      return this.$store.getters.getUser
    }

  },
  filters: {
    dateFormat (d) {
      return formatLocalDate(d)
    },
    sprintState (s) {
      return s.state === 'closed' ? `${s.state} on ${formatLocalDate(s.completeDate)}` : s.state
    }
  },
  mounted () {
    this.checkIfHasAccessToken()
  },
  methods: {
    checkIfHasAccessToken () {
      this.axios.get('/api/check-jira-api-access', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.hasAccess = !!(res.data && res.data[0] && res.data[0].Has_Access)
        this.showJiraAccessTokenDialog = !this.hasAccess
      }).catch(error => {
        this.hasAccess = false
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    onTokenSaved () {
      this.hasAccess = true
      this.checkIfHasAccessToken()
    },
    getProjects () {
      if (!this.hasAccess) return
      this.boards = []
      this.sprints = []
      this.velocity = null
      this.selectedBoard = null
      this.selectedSprint = null
      this.storyPointFieldId = null
      this.axios.post('/api/jira-velocity/get-projects', {
        url: this.jiraUrl
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.projects = res.data.values || []
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getBoards () {
      if (!this.hasAccess) return
      this.sprints = []
      this.velocity = null
      this.selectedSprint = null
      this.axios.post('/api/jira-velocity/get-boards', {
        url: this.jiraUrl,
        projectKey: this.selectedProject
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.boards = res.data.values || []
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getSprints () {
      if (!this.hasAccess) return
      this.velocity = null
      this.axios.post('/api/jira-velocity/get-sprints', {
        url: this.jiraUrl,
        boardId: this.selectedBoard
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.sprints = res.data.values || []
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async getStoryPointFieldId () {
      return this.axios.post('/api/jira-velocity/get-fields', {
        url: this.jiraUrl
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.storyPointFieldId = res.data.id
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    async getVelocity () {
      if (!this.hasAccess) return
      if (!this.storyPointFieldId) {
        await this.getStoryPointFieldId()
      }
      this.axios.post('/api/jira-velocity/get-issues', {
        url: this.jiraUrl,
        sprintId: this.selectedSprint,
        fieldId: this.storyPointFieldId
      },
      {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.velocity = res.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
<style lang="scss" scoped>
   .sticky-col, .v-item--active {
     position: sticky;
     top: 0;
     z-index: 1;
   }
   .v-item--active {
     background-color: #fff;
   }
</style>
